import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, Body3Regular, CaptionLight } from '../../lib/font';
import {
    useTeams,
    useCreateTeam,
    useDeleteTeam,
    useCenterSalesManagers,
} from '@/features/team';
import type { CenterSalesManager, CreateTeamBody, TeamWithSummary } from '@/features/team';
import { salesManagerToUserType } from '@/features/team';

const formatKRW = (n: number) => `${(n ?? 0).toLocaleString()}원`;

interface CreateTeamFormState {
    name: string;
    description: string;
    leaderUserId: string;
    selectedMemberIds: Set<string>; // 팀원 userId set
    effectiveFrom: string; // YYYY-MM-DD
}

const makeInitialForm = (): CreateTeamFormState => ({
    name: '',
    description: '',
    leaderUserId: '',
    selectedMemberIds: new Set(),
    effectiveFrom: '',
});

const TeamListPage = ({ createAlert }: { createAlert: any }) => {
    const navigate = useNavigate();
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId: string = authObject?.centerId;

    const [statusFilter, setStatusFilter] = useState<'active' | 'all'>('active');
    const { data, isLoading } = useTeams({ centerId, status: statusFilter });
    const createMutation = useCreateTeam(centerId);
    const deleteMutation = useDeleteTeam(centerId);

    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [form, setForm] = useState<CreateTeamFormState>(makeInitialForm);
    const [deleteTarget, setDeleteTarget] = useState<TeamWithSummary | null>(null);

    // 판매담당자 목록 — 표시용(all: 퇴사 포함) / 선택 옵션(active: 활성만) 분리
    const { data: smData } = useCenterSalesManagers({ centerId });
    const allManagers = smData?.all ?? [];
    const activeManagers = smData?.active ?? [];
    const userNameById = useMemo(
        () => new Map(allManagers.map((m) => [m.userId, m.userName])),
        [allManagers],
    );

    const teams: TeamWithSummary[] = data?.teams ?? [];

    const totals = useMemo(() => {
        let total = 0;
        let memberCount = 0;
        for (const t of teams) {
            if (!t.isActive) continue;
            total += t.monthRevenue?.total ?? 0;
            memberCount += t.memberCount ?? 0;
        }
        // 인당 평균 — 본사 팀 분석(avgPerMember)과 같은 식: 매출 합 / 활성 멤버 수(팀장 포함)
        const avgPerMember = memberCount > 0 ? Math.round(total / memberCount) : 0;
        return { total, memberCount, avgPerMember, teamCount: teams.filter((t) => t.isActive).length };
    }, [teams]);

    const toggleMember = (userId: string) => {
        setForm((prev) => {
            const next = new Set(prev.selectedMemberIds);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return { ...prev, selectedMemberIds: next };
        });
    };

    const handleCreate = async () => {
        if (!form.name.trim()) {
            createAlert('입력 오류', '팀명은 필수입니다.', true);
            return;
        }
        if (!form.leaderUserId) {
            createAlert('입력 오류', '팀장을 선택하세요.', true);
            return;
        }
        const leader = activeManagers.find((m) => m.userId === form.leaderUserId);
        if (!leader) {
            createAlert('입력 오류', '팀장 정보를 찾을 수 없습니다.', true);
            return;
        }
        const memberIds = Array.from(form.selectedMemberIds).filter(
            (id) => id !== form.leaderUserId,
        );
        const memberInputs = memberIds
            .map((id) => activeManagers.find((m) => m.userId === id))
            .filter((m): m is CenterSalesManager => Boolean(m))
            .map((m) => ({ userId: m.userId, userType: salesManagerToUserType(m.role) }));

        const body: CreateTeamBody = {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            leader: { userId: leader.userId, userType: salesManagerToUserType(leader.role) },
            members: memberInputs,
            effectiveFrom: form.effectiveFrom ? new Date(form.effectiveFrom).toISOString() : undefined,
        };
        try {
            const res = await createMutation.mutateAsync(body);
            const bf = res.data?.backfillResult;
            const updated =
                (bf?.memberTicketPayment?.updated ?? 0) + (bf?.membershipPayment?.updated ?? 0);
            createAlert(
                '팀 생성 완료',
                updated > 0
                    ? `팀이 생성되었고 과거 매출 ${updated}건이 합산되었습니다.`
                    : '팀이 생성되었습니다.',
                false,
            );
            setIsCreateOpen(false);
            setForm(makeInitialForm());
        } catch (e: any) {
            createAlert('팀 생성 실패', e?.message ?? '서버 오류가 발생했습니다.', true);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync(deleteTarget._id);
            createAlert('팀 해체 완료', `'${deleteTarget.name}' 팀이 해체되었습니다.`, false);
            setDeleteTarget(null);
        } catch (e: any) {
            createAlert('해체 실패', e?.message ?? '서버 오류가 발생했습니다.', true);
        }
    };

    return (
        <Col style={{ width: '100%', padding: '112px 48px 48px', gap: 28 }}>
            {/* 뒤로 가기 */}
            <button
                onClick={() => navigate('/trainerManage')}
                style={{
                    alignSelf: 'flex-start',
                    padding: '4px 0',
                    background: 'none',
                    border: 'none',
                    color: colors.GREY_400,
                    fontSize: 14,
                    cursor: 'pointer',
                }}
            >
                ← 강사 관리로 돌아가기
            </button>

            {/* 헤더 */}
            <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Col style={{ gap: 6 }}>
                    <Subtitle1Bold style={{ color: colors.GREY_900 }}>PT팀 관리</Subtitle1Bold>
                    <Body3Regular style={{ color: colors.GREY_300 }}>
                        팀을 만들고 팀장·팀원을 배치하면 그 시점부터 매출이 팀에 합산됩니다.
                    </Body3Regular>
                </Col>
                <PrimaryButton onClick={() => setIsCreateOpen(true)}>+ 팀 만들기</PrimaryButton>
            </Row>

            {/* 요약 카드 */}
            <Row style={{ gap: 16 }}>
                <SummaryCard label="활성 팀 수" value={`${totals.teamCount}개`} />
                <SummaryCard label="총 팀원 수" value={`${totals.memberCount}명`} />
                <SummaryCard label="이번 달 팀 합산 매출" value={formatKRW(totals.total)} />
                <SummaryCard label="이번 달 인당 평균 매출" value={formatKRW(totals.avgPerMember)} />
            </Row>

            {/* 필터 */}
            <Row style={{ gap: 8 }}>
                <FilterChip
                    label="활성 팀만"
                    active={statusFilter === 'active'}
                    onClick={() => setStatusFilter('active')}
                />
                <FilterChip
                    label="해체 포함"
                    active={statusFilter === 'all'}
                    onClick={() => setStatusFilter('all')}
                />
            </Row>

            {/* 팀 리스트 */}
            {isLoading ? (
                <Body2Regular style={{ color: colors.GREY_300 }}>로딩 중...</Body2Regular>
            ) : teams.length === 0 ? (
                <IntroGuide onCreate={() => setIsCreateOpen(true)} />
            ) : (
                <Col style={{ gap: 12 }}>
                    {teams.map((t) => (
                        <Row
                            key={t._id}
                            style={{
                                padding: 20,
                                background: t.isActive ? colors.white : colors.Gray,
                                border: `1px solid ${colors.GREY_50}`,
                                borderRadius: 8,
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                opacity: t.isActive ? 1 : 0.6,
                            }}
                            onClick={() => navigate(`/teams/${t._id}`)}
                        >
                            <Col style={{ gap: 6 }}>
                                <Row style={{ gap: 8, alignItems: 'center' }}>
                                    <Subtitle1Bold style={{ color: colors.GREY_900 }}>{t.name}</Subtitle1Bold>
                                    {!t.isActive && (
                                        <Body3Regular style={{ color: colors.GREY_200 }}>
                                            (해체됨)
                                        </Body3Regular>
                                    )}
                                </Row>
                                {t.description && (
                                    <Body3Regular style={{ color: colors.GREY_300 }}>
                                        {t.description}
                                    </Body3Regular>
                                )}
                                <Body3Regular style={{ color: colors.GREY_400 }}>
                                    팀원 {t.memberCount}명 · 팀장 {userNameById.get(t.leaderUserId) ?? t.leaderUserId}
                                </Body3Regular>
                            </Col>
                            <Row style={{ gap: 24, alignItems: 'center' }}>
                                <Col style={{ alignItems: 'flex-end', gap: 4 }}>
                                    <Body3Regular style={{ color: colors.GREY_300 }}>인당 평균</Body3Regular>
                                    <Subtitle1Bold style={{ color: colors.GREY_900 }}>
                                        {formatKRW(
                                            t.memberCount > 0
                                                ? Math.round((t.monthRevenue?.total ?? 0) / t.memberCount)
                                                : 0,
                                        )}
                                    </Subtitle1Bold>
                                </Col>
                                <Col style={{ alignItems: 'flex-end', gap: 4 }}>
                                    <Body3Regular style={{ color: colors.GREY_300 }}>이번 달 매출</Body3Regular>
                                    <Subtitle1Bold style={{ color: colors.PRIMARY_BLUE }}>
                                        {formatKRW(t.monthRevenue?.total ?? 0)}
                                    </Subtitle1Bold>
                                </Col>
                                {t.isActive && (
                                    <SecondaryButton
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            setDeleteTarget(t);
                                        }}
                                        textColor={colors.red}
                                    >
                                        해체
                                    </SecondaryButton>
                                )}
                            </Row>
                        </Row>
                    ))}
                </Col>
            )}

            {/* 생성 모달 */}
            {isCreateOpen && (
                <ModalContainer onClose={() => setIsCreateOpen(false)}>
                    <Col style={{ gap: 20, padding: 32, width: 600, maxHeight: '85vh', overflow: 'auto' }}>
                        <Subtitle1Bold style={{ color: colors.GREY_900 }}>팀 만들기</Subtitle1Bold>

                        <FieldInput
                            label="팀명 *"
                            value={form.name}
                            onChange={(v) => setForm({ ...form, name: v })}
                            placeholder="예: 송파지점 1팀"
                        />
                        <FieldInput
                            label="설명 (선택)"
                            value={form.description}
                            onChange={(v) => setForm({ ...form, description: v })}
                        />

                        {/* 팀장 선택 */}
                        <Col style={{ gap: 6 }}>
                            <Body3Regular style={{ color: colors.GREY_400 }}>팀장 *</Body3Regular>
                            {activeManagers.length === 0 ? (
                                <Body3Regular style={{ color: colors.GREY_300 }}>
                                    판매담당자 목록 로딩 중...
                                </Body3Regular>
                            ) : (
                                <select
                                    value={form.leaderUserId}
                                    onChange={(e) =>
                                        setForm({ ...form, leaderUserId: e.target.value })
                                    }
                                    style={{
                                        padding: '12px 14px',
                                        border: `1px solid ${colors.GREY_50}`,
                                        borderRadius: 6,
                                        fontSize: 16,
                                    }}
                                >
                                    <option value="">— 팀장 선택 —</option>
                                    {activeManagers.map((m) => (
                                        <option key={m.userId} value={m.userId}>
                                            {m.userName} {m.role === 'coach' ? '(강사)' : '(판매)'}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </Col>

                        {/* 팀원 선택 (팀장 외) — 강사 + 판매담당 통합 */}
                        <Col style={{ gap: 6 }}>
                            <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <Body3Regular style={{ color: colors.GREY_400 }}>
                                    팀원 (
                                    {
                                        Array.from(form.selectedMemberIds).filter(
                                            (id) => id !== form.leaderUserId,
                                        ).length
                                    }
                                    명 선택)
                                </Body3Regular>
                                {activeManagers.length > 0 && (
                                    <button
                                        onClick={() => {
                                            const others = activeManagers
                                                .filter((m) => m.userId !== form.leaderUserId)
                                                .map((m) => m.userId);
                                            const allSelected = others.every((id) =>
                                                form.selectedMemberIds.has(id),
                                            );
                                            setForm({
                                                ...form,
                                                selectedMemberIds: allSelected
                                                    ? new Set()
                                                    : new Set(others),
                                            });
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: colors.PRIMARY_BLUE,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        전체 선택/해제
                                    </button>
                                )}
                            </Row>
                            <Col
                                style={{
                                    border: `1px solid ${colors.GREY_50}`,
                                    borderRadius: 6,
                                    maxHeight: 240,
                                    overflowY: 'auto',
                                }}
                            >
                                {activeManagers.length === 0 ? (
                                    <Body3Regular style={{ color: colors.GREY_300, padding: 16 }}>
                                        판매담당자 목록을 불러오는 중...
                                    </Body3Regular>
                                ) : (
                                    activeManagers.map((m: CenterSalesManager) => {
                                        const isLeader = m.userId === form.leaderUserId;
                                        const isChecked = isLeader || form.selectedMemberIds.has(m.userId);
                                        return (
                                            <Row
                                                key={m.userId}
                                                onClick={() => !isLeader && toggleMember(m.userId)}
                                                style={{
                                                    padding: '12px 16px',
                                                    borderBottom: `1px solid ${colors.GREY_50}`,
                                                    alignItems: 'center',
                                                    gap: 12,
                                                    cursor: isLeader ? 'default' : 'pointer',
                                                    background: isLeader ? colors.BLUE_50 : 'transparent',
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isLeader}
                                                    onChange={() => {}}
                                                    style={{ width: 16, height: 16 }}
                                                />
                                                <Body2Regular style={{ color: colors.GREY_900, flex: 1 }}>
                                                    {m.userName}
                                                </Body2Regular>
                                                <span
                                                    style={{
                                                        padding: '3px 10px',
                                                        background: m.role === 'coach' ? colors.BLUE_50 : colors.WHITE_300,
                                                        color: m.role === 'coach' ? colors.PRIMARY_BLUE : colors.GREY_400,
                                                        borderRadius: 10,
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {m.role === 'coach' ? '강사' : '판매'}
                                                </span>
                                                {isLeader && (
                                                    <span
                                                        style={{
                                                            fontSize: 13,
                                                            color: colors.PRIMARY_BLUE,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        팀장
                                                    </span>
                                                )}
                                            </Row>
                                        );
                                    })
                                )}
                            </Col>
                        </Col>

                        <Col style={{ gap: 6 }}>
                            <Body3Regular style={{ color: colors.GREY_400 }}>
                                집계 시작일{' '}
                                <span style={{ color: colors.GREY_300, fontSize: 13 }}>
                                    (미입력 시 이번 달 1일부터 자동 합산)
                                </span>
                            </Body3Regular>
                            <Row style={{ gap: 8, alignItems: 'center' }}>
                                <input
                                    type="date"
                                    value={form.effectiveFrom}
                                    onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                                    style={{
                                        flex: 1,
                                        padding: '12px 14px',
                                        border: `1px solid ${colors.GREY_50}`,
                                        borderRadius: 6,
                                        fontSize: 16,
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const d = new Date();
                                        d.setDate(1);
                                        setForm({
                                            ...form,
                                            effectiveFrom: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
                                        });
                                    }}
                                    style={{
                                        padding: '8px 12px',
                                        border: `1px solid ${colors.GREY_50}`,
                                        background: colors.white,
                                        color: colors.GREY_400,
                                        borderRadius: 6,
                                        fontSize: 13,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    이번 달 1일
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const d = new Date();
                                        d.setMonth(d.getMonth() - 1);
                                        d.setDate(1);
                                        setForm({
                                            ...form,
                                            effectiveFrom: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
                                        });
                                    }}
                                    style={{
                                        padding: '8px 12px',
                                        border: `1px solid ${colors.GREY_50}`,
                                        background: colors.white,
                                        color: colors.GREY_400,
                                        borderRadius: 6,
                                        fontSize: 13,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    지난 달부터
                                </button>
                            </Row>
                        </Col>

                        <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                            <SecondaryButton onClick={() => setIsCreateOpen(false)}>취소</SecondaryButton>
                            <PrimaryButton onClick={handleCreate} disabled={createMutation.isLoading}>
                                {createMutation.isLoading ? '생성 중...' : '팀 생성'}
                            </PrimaryButton>
                        </Row>
                    </Col>
                </ModalContainer>
            )}

            {/* 해체 확인 */}
            {deleteTarget && (
                <ConfirmModal
                    title="팀 해체"
                    description={`'${deleteTarget.name}' 팀을 해체합니다. 과거 매출 기록은 보존됩니다.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    confirmText="해체"
                />
            )}
        </Col>
    );
};

const PrimaryButton = ({
    children,
    onClick,
    disabled,
}: {
    children: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    disabled?: boolean;
}) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            padding: '12px 22px',
            background: disabled ? colors.WHITE_700 : colors.PRIMARY_BLUE,
            color: colors.white,
            border: 'none',
            borderRadius: 6,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: 16,
            fontWeight: 600,
        }}
    >
        {children}
    </button>
);

const SecondaryButton = ({
    children,
    onClick,
    textColor = colors.GREY_400,
}: {
    children: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
    textColor?: string;
}) => (
    <button
        onClick={onClick}
        style={{
            padding: '10px 18px',
            background: colors.white,
            color: textColor,
            border: `1px solid ${colors.GREY_50}`,
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 500,
        }}
    >
        {children}
    </button>
);

const ConfirmModal = ({
    title,
    description,
    onConfirm,
    onCancel,
    confirmText = '확인',
}: {
    title: string;
    description: string;
    onConfirm: () => void;
    onCancel: () => void;
    confirmText?: string;
}) => (
    <ModalContainer onClose={onCancel}>
        <Col style={{ padding: 32, gap: 20, width: 460 }}>
            <Subtitle1Bold style={{ color: colors.GREY_900 }}>{title}</Subtitle1Bold>
            <Body2Regular style={{ color: colors.GREY_400 }}>{description}</Body2Regular>
            <Row style={{ gap: 10, justifyContent: 'flex-end' }}>
                <SecondaryButton onClick={onCancel}>취소</SecondaryButton>
                <PrimaryButton onClick={onConfirm}>{confirmText}</PrimaryButton>
            </Row>
        </Col>
    </ModalContainer>
);

// === 보조 컴포넌트 ===

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
    <Col
        style={{
            flex: 1,
            padding: '24px 22px',
            background: colors.white,
            border: `1px solid ${colors.GREY_50}`,
            borderRadius: 8,
            gap: 10,
        }}
    >
        <Body3Regular style={{ color: colors.GREY_300 }}>{label}</Body3Regular>
        <Subtitle1Bold style={{ color: colors.GREY_900 }}>{value}</Subtitle1Bold>
    </Col>
);

const FilterChip = ({
    label,
    active,
    onClick,
}: {
    label: string;
    active: boolean;
    onClick: () => void;
}) => (
    <button
        onClick={onClick}
        style={{
            padding: '8px 18px',
            border: `1px solid ${active ? colors.PRIMARY_BLUE : colors.GREY_50}`,
            background: active ? colors.PRIMARY_BLUE : colors.white,
            color: active ? colors.white : colors.GREY_400,
            borderRadius: 18,
            fontSize: 15,
            fontWeight: active ? 600 : 400,
            cursor: 'pointer',
        }}
    >
        {label}
    </button>
);

const FieldInput = ({
    label,
    value,
    onChange,
    placeholder,
    type = 'text',
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
}) => (
    <Col style={{ gap: 6, flex: 1 }}>
        <Body3Regular style={{ color: colors.GREY_400 }}>{label}</Body3Regular>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            style={{
                padding: '12px 14px',
                border: `1px solid ${colors.GREY_50}`,
                borderRadius: 6,
                fontSize: 16,
            }}
        />
    </Col>
);

const ModalContainer = ({ children, onClose }: { children: React.ReactNode; onClose: () => void }) => (
    <div
        onClick={onClose}
        style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        }}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: colors.white, borderRadius: 12, maxHeight: '90vh', overflow: 'auto' }}
        >
            {children}
        </div>
    </div>
);

const IntroGuide = ({ onCreate }: { onCreate: () => void }) => (
    <Col
        style={{
            background: 'linear-gradient(135deg, #ECF1FC 0%, #FFFFFF 60%)',
            border: `1px solid ${colors.BLUE_100}`,
            borderRadius: 16,
            padding: '40px 48px',
            gap: 32,
        }}
    >
        <Col style={{ gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 36 }}>🏆</span>
            <Subtitle1Bold style={{ color: colors.BLUE_900, fontSize: 24 }}>
                PT팀으로 매출 관리해보세요
            </Subtitle1Bold>
            <Body3Regular style={{ color: colors.GREY_400, lineHeight: 1.6 }}>
                강사와 판매담당자를 팀으로 묶어, 팀별 매출을 자동으로 합산·비교합니다.
                <br />
                본사는 모든 센터의 팀 성과를 한 화면에서 분석할 수 있어
                팀 단위 운영 인사이트를 확보할 수 있습니다.
            </Body3Regular>
        </Col>

        <Row style={{ gap: 16 }}>
            <GuideStep
                num={1}
                title="팀 구성"
                desc={'센터의 강사·판매담당자 중에서\n팀장과 팀원을 선택해 팀을 만듭니다.'}
            />
            <GuideStep
                num={2}
                title="자동 매출 합산"
                desc={'팀 생성 시점부터의 PT+그룹수업 결제가\n팀 매출에 자동으로 박제됩니다.'}
            />
            <GuideStep
                num={3}
                title="비교 분석"
                desc={'센터 내·프랜차이즈 전체 팀 매출을\n실시간으로 랭킹·비교할 수 있습니다.'}
            />
        </Row>

        <Col
            style={{
                padding: 18,
                background: colors.white,
                border: `1px solid ${colors.WHITE_600}`,
                borderRadius: 10,
                gap: 8,
            }}
        >
            <Body3Regular style={{ color: colors.GREY_900, fontWeight: 600 }}>
                💡 알아두면 좋은 점
            </Body3Regular>
            <Body3Regular style={{ color: colors.GREY_400, lineHeight: 1.7 }}>
                · <b>과거 매출도 합산 가능</b>: 팀 생성 시 "집계 시작일"을 과거 날짜로 지정하면 그 시점부터의 결제가 소급 합산됩니다.
                <br />· <b>스냅샷 정책</b>: 한 번 박제된 매출은 강사가 팀을 옮기거나 팀이 해체돼도 그대로 보존됩니다.
                <br />· <b>1인 1팀</b>: 한 사람은 동시에 한 팀에만 소속될 수 있습니다. (다른 팀 이동은 명시적 확인 필요)
                <br />· <b>매출 범위</b>: PT + 그룹수업이 합산되고, 시설이용권은 제외됩니다.
            </Body3Regular>
        </Col>

        <Row style={{ justifyContent: 'flex-start' }}>
            <PrimaryButton onClick={onCreate}>+ 첫 팀 만들기</PrimaryButton>
        </Row>
    </Col>
);

const GuideStep = ({ num, title, desc }: { num: number; title: string; desc: string }) => (
    <Col
        style={{
            flex: 1,
            padding: 22,
            background: colors.white,
            border: `1px solid ${colors.WHITE_600}`,
            borderRadius: 12,
            gap: 10,
        }}
    >
        <span
            style={{
                display: 'inline-flex',
                width: 32,
                height: 32,
                borderRadius: 16,
                background: colors.PRIMARY_BLUE,
                color: colors.white,
                fontWeight: 700,
                fontSize: 15,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {num}
        </span>
        <Body2Regular style={{ color: colors.GREY_900, fontWeight: 700 }}>{title}</Body2Regular>
        <Body3Regular style={{ color: colors.GREY_400, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            {desc}
        </Body3Regular>
    </Col>
);

export default TeamListPage;
